import React from 'react';
import { Helmet } from 'react-helmet';
import { withToastManager } from 'react-toast-notifications';
import H1 from 'components/H1';
import { Flex, Box, Button } from 'rebass';
import { Label } from '@rebass/forms';
import services from 'services/services';
import styled from 'styled-components';
import config from 'config';
import auth from 'services/auth';
import ReactTooltip from 'react-tooltip';
import TooltipIcon from 'components/TooltipIcon';
import integrations from 'services/integrations';
import consts from 'consts';

import AppPageWrapper from 'components/AppPageWrapper';
import AppPageWrapperSectionHeader from 'components/AppPageWrapper/AppPageWrapperSectionHeader';
import AppPageWrapperSectionBody from 'components/AppPageWrapper/AppPageWrapperSectionBody';
import SettingsPageSubNav from 'containers/SettingsPage/SettingsPageSubNav';
import AppPageSubnav from 'components/AppPageWrapper/AppPageSubnav';

const IntegrationItem = styled(Flex)`
    width: 800px;
    border-radius: 4px;
    border: 1px solid #eee;
    padding: 12px 16px 16px;
    margin: 0 0 16px 0;
    line-height: 32px;

    .integration-title {
        font-weight: 500;
        color: black;
        display: flex;
        justify-content: space-between;
        align-items: auto;
        vertical-align: top;

        img {
            height: 32px;
            margin: 6px 12px 0 0;
        }
    }
    .integration-action {
        text-align: right;
    }
`;

class SettingsIntegrationsPage extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            account: this.props.auth.account
        };

        this.calendlyAuthorizationUrl = `${
            config.API_URL
        }/integrations/calendly/authorize-redirect?account_id=${auth.getAccountId()}`;
        this.tremendousAuthorizationUrl = `${
            config.API_URL
        }/integrations/tremendous/authorize-redirect?account_id=${auth.getAccountId()}`;
        this.disconnectZoom = this.disconnectZoom.bind(this);
        this.disconnectCalendly = this.disconnectCalendly.bind(this);
        this.disconnectTremendous = this.disconnectTremendous.bind(this);
    }

    disconnectZoom() {
        const { toastManager } = this.props;

        integrations
            .disconnectZoomAccount()
            .then(() => {
                toastManager.add('Disconnected Zoom', {
                    appearance: 'success',
                    autoDismiss: true
                });

                auth.checkAuth();

                const prevAccount = this.state.account;

                this.setState({
                    account: {
                        ...prevAccount,
                        zoom_account_id: null
                    }
                });
            })
            .catch(error => {
                const errorText = services.parseXhrErrors(error);

                toastManager.add(errorText, {
                    appearance: 'error',
                    autoDismiss: true
                });
            });
    }

    disconnectCalendly() {
        const { toastManager } = this.props;

        integrations
            .disconnectCalendlyAccount()
            .then(account => {
                toastManager.add('Disconnected Calendly', {
                    appearance: 'success',
                    autoDismiss: true
                });

                auth.checkAuth();

                this.setState({
                    account
                });
            })
            .catch(error => {
                const errorText = services.parseXhrErrors(error);

                toastManager.add(errorText, {
                    appearance: 'error',
                    autoDismiss: true
                });
            });
    }

    disconnectTremendous() {
        const { toastManager } = this.props;

        integrations
            .disconnectTremendousAccount()
            .then(account => {
                toastManager.add('Disconnected Tremendous', {
                    appearance: 'success',
                    autoDismiss: true
                });

                auth.checkAuth();

                this.setState({
                    account
                });
            })
            .catch(error => {
                const errorText = services.parseXhrErrors(error);

                toastManager.add(errorText, {
                    appearance: 'error',
                    autoDismiss: true
                });
            });
    }

    render() {
        return (
            <Flex style={{ width: '100%' }}>
                <AppPageWrapper>
                    <Helmet>
                        <title>Integrations</title>
                    </Helmet>

                    <AppPageSubnav>
                        <SettingsPageSubNav/>
                    </AppPageSubnav>

                    <AppPageWrapperSectionHeader className='experience'>
                        <Box>
                            <H1>Integrations</H1>
                            <Box className='experience-description'>
                                Automate tasks with our integrations.
                            </Box>
                        </Box>
                    </AppPageWrapperSectionHeader>
                    <AppPageWrapperSectionBody>
                        <Box mx={4} my={3}>
                            <IntegrationItem>
                                <Box width={3/4} className="integration-title">
                                    <Box>
                                        <img src="/zoom.png" alt="Zoom" />
                                    
                                        Zoom<br/>
                                        <Label className='quiet' ml={'44px'}>Automatically add a Zoom meeting to scheduled sessions.</Label>
                                    </Box>
                                </Box>
                                <Box width={1/4} className="integration-action">
                                    {this.state.account.zoom_account_id ? (
                                        <Button variant="link" className="muted" onClick={this.disconnectZoom}>
                                            Disconnect
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="link"
                                            onClick={() => {
                                                window.location = consts.zoomAuthorizationUrl;
                                            }}
                                        >
                                            Connect
                                        </Button>
                                    )}
                                </Box>
                            </IntegrationItem>
                            <br />
                            <IntegrationItem>
                                <Box width={3/4} className="integration-title">
                                    <Box>
                                        <img src="/calendly.png" alt="Calendly" />
                                    
                                        Calendly Pro<br/>
                                        <Label className='quiet' ml={'44px'}>
                                            Enable Calendly to handle the scheduling. Ensure to update Study > Scheduling Availability > Scheduling Tool to "Calendly".
                                        </Label>
                                    </Box>
                                </Box>
                                <Box width={1/4} className="integration-action">
                                    {this.state.account.calendly_access_token ? (
                                        <>
                                            <Button variant="link" className="muted" onClick={this.disconnectCalendly}>
                                                Disconnect
                                            </Button>
                                            <br />
                                            <small>
                                                Integrated user role:{' '}
                                                {this.state.account.calendly_integrating_membership_role}
                                            </small>

                                            {/*! ['owner','admin'].includes(this.state.account.calendly_integrating_membership_role) ? 'This membership does not allow the integration to view all users events': '' */}
                                        </>
                                    ) : (
                                        <Button
                                            variant="link"
                                            onClick={() => {
                                                window.location = this.calendlyAuthorizationUrl;
                                            }}
                                        >
                                            Connect
                                        </Button>
                                    )}
                                </Box>
                            </IntegrationItem>


                            <br />
                            <IntegrationItem>
                                <Box width={3/4} className="integration-title">
                                    <Box>
                                        <img src="/tremendous.png" alt="Tremendous" />
                                    
                                        Tremendous<br/>
                                        <Label className='quiet' ml={'44px'}>
                                            <span>
                                                Enable Tremendous to handle incentive payments.
                                                You must request "production API access" from Tremendous (developers@tremendous.com) for this integration to connect.
                                                &nbsp;<a href="#" data-beacon-article="62f686cc829a3853b69263db">How to set up Tremendous.</a>
                                            </span>
                                        </Label>
                                    </Box>
                                </Box>
                                <Box width={1/4} className="integration-action">
                                    {this.state.account.tremendous_access_token ? (
                                        <>
                                            <Button variant="link" className="muted" onClick={this.disconnectTremendous}>
                                                Disconnect
                                            </Button>
                                        </>
                                    ) : (
                                        <Button
                                            variant="link"
                                            onClick={() => {
                                                window.location = this.tremendousAuthorizationUrl;
                                            }}
                                        >
                                            Connect
                                        </Button>
                                    )}
                                </Box>
                            </IntegrationItem>
                        </Box>
                    </AppPageWrapperSectionBody>
                </AppPageWrapper>
            </Flex>
        );
    }
}

export default withToastManager(SettingsIntegrationsPage);
